import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  deleteDoc,
  CollectionReference,
  DocumentData,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Debt, DebtPayment, DebtSourceType, DebtStatus, CustomerDebtSummary } from '../utils/debtTypes';
import { Customer } from '../types/customer';
import { getCustomerById, updateCustomer } from './customerService';
import { isLocalMode, getLocalList, setLocalList, generateLocalId, getLocalObject, setLocalObject } from '../utils/localModeHelper';

type DebtUpdateCallback = () => void;
const debtUpdateCallbacks: DebtUpdateCallback[] = [];

export const onDebtUpdate = (callback: DebtUpdateCallback) => {
  debtUpdateCallbacks.push(callback);
  return () => {
    const index = debtUpdateCallbacks.indexOf(callback);
    if (index > -1) {
      debtUpdateCallbacks.splice(index, 1);
    }
  };
};

const notifyDebtUpdate = () => {
  debtUpdateCallbacks.forEach(callback => {
    try { callback(); } catch (error) {
      console.error('Error in debt update callback:', error);
    }
  });
};

const ensureFirestore = (): Firestore => {
  if (!db) throw new Error('خدمة Firestore غير متوفرة');
  return db;
};

const getDebtsCollection = (): CollectionReference<DocumentData> =>
  collection(ensureFirestore(), 'debts');

const getDebtPaymentsCollection = (): CollectionReference<DocumentData> =>
  collection(ensureFirestore(), 'debtPayments');

const DEBTS_LS_KEY = 'debts_local';
const PAYMENTS_LS_KEY = 'debt_payments_local';
const DRAWER_TX_LS_KEY = 'drawer_transactions_local';

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val instanceof Timestamp) return val.toDate();
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

export const createDebt = async (
  customerId: string,
  amount: number,
  sourceType: DebtSourceType,
  sourceId: string,
  sourceReference: string,
  description: string,
  createdBy: string,
  createdByName: string,
  branchId: string,
  branchName: string,
  dueDate?: Date,
  notes?: string
): Promise<Debt> => {
  try {
    const customer = await getCustomerById(customerId);
    if (!customer) throw new Error('العميل غير موجود');

    const now = new Date();
    const debtData: Debt = {
      id: '',
      customerId,
      customerName: customer.name,
      customerPhone: customer.phone,
      amount,
      remainingAmount: amount,
      paidAmount: 0,
      sourceType,
      sourceId,
      sourceReference,
      description,
      dueDate: dueDate || undefined,
      status: DebtStatus.ACTIVE,
      createdAt: now,
      updatedAt: now,
      createdBy,
      createdByName,
      branchId,
      branchName,
      notes: notes || ''
    };

    if (isLocalMode()) {
      const newDebt: Debt = { ...debtData, id: generateLocalId('debt') };
      const list = getLocalList<Debt>(DEBTS_LS_KEY);
      list.push(newDebt);
      setLocalList(DEBTS_LS_KEY, list);
      await updateCustomerDebtInfo(customerId);
      return newDebt;
    }

    const debtsCollection = getDebtsCollection();
    const debtRef = await addDoc(debtsCollection, {
      ...debtData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      dueDate: dueDate ? Timestamp.fromDate(new Date(dueDate)) : null
    });

    await updateCustomerDebtInfo(customerId);

    return { ...debtData, id: debtRef.id };
  } catch (error) {
    console.error('Error creating debt:', error);
    throw error;
  }
};

export const getDebtById = async (debtId: string): Promise<Debt | null> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<Debt>(DEBTS_LS_KEY);
      return list.find(d => d.id === debtId) || null;
    }

    const firestore = ensureFirestore();
    const debtDoc = await getDoc(doc(firestore, 'debts', debtId));

    if (debtDoc.exists()) {
      const data = debtDoc.data();
      return {
        id: debtDoc.id,
        ...data,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        dueDate: data.dueDate ? toDate(data.dueDate) : (data.dueDate || null)
      } as Debt;
    }
    return null;
  } catch (error) {
    console.error('Error getting debt:', error);
    return null;
  }
};

export const getAllDebts = async (statusFilter?: DebtStatus, limitCount: number = 100): Promise<Debt[]> => {
  try {
    if (isLocalMode()) {
      let list = getLocalList<Debt>(DEBTS_LS_KEY);
      if (statusFilter) list = list.filter(d => d.status === statusFilter);
      return list
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime())
        .slice(0, limitCount);
    }

    let q;
    if (statusFilter) {
      q = query(getDebtsCollection(), where('status', '==', statusFilter), orderBy('createdAt', 'desc'), limit(limitCount));
    } else {
      q = query(getDebtsCollection(), orderBy('createdAt', 'desc'), limit(limitCount));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        dueDate: data.dueDate ? toDate(data.dueDate) : (data.dueDate || null)
      } as Debt;
    });
  } catch (error) {
    console.error('Error getting debts:', error);
    return [];
  }
};

export const getCustomerDebts = async (customerId: string): Promise<Debt[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<Debt>(DEBTS_LS_KEY).filter(d => d.customerId === customerId);
      return list.sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
    }

    const q = query(getDebtsCollection(), where('customerId', '==', customerId), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        dueDate: data.dueDate ? toDate(data.dueDate) : (data.dueDate || null)
      } as Debt;
    });
  } catch (error) {
    console.error('Error getting customer debts:', error);
    return [];
  }
};

export const getCustomerDebtSummary = async (customerId: string): Promise<CustomerDebtSummary | null> => {
  try {
    const customer = await getCustomerById(customerId);
    if (!customer) throw new Error('العميل غير موجود');

    const debts = await getCustomerDebts(customerId);
    const totalDebt = debts.reduce((sum, d) => sum + d.amount, 0);
    const activeDebts = debts.filter(d => d.status === DebtStatus.ACTIVE || d.status === DebtStatus.PARTIALLY_PAID);
    const activeDebt = activeDebts.reduce((sum, d) => sum + d.remainingAmount, 0);
    const paidDebt = debts.filter(d => d.status === DebtStatus.PAID).reduce((sum, d) => sum + d.amount, 0);

    const payments = await getCustomerDebtPayments(customerId);
    const lastPayment = payments.length > 0 ? payments[0] : null;

    return {
      customerId,
      customerName: customer.name,
      totalDebt,
      activeDebt,
      paidDebt,
      lastPaymentDate: lastPayment ? lastPayment.paymentDate : undefined,
      debtsCount: debts.length
    };
  } catch (error) {
    console.error('Error getting customer debt summary:', error);
    return null;
  }
};

export const updateCustomerDebtInfo = async (customerId: string): Promise<void> => {
  try {
    const debtSummary = await getCustomerDebtSummary(customerId);
    if (!debtSummary) return;

    await updateCustomer(customerId, {
      hasDebts: debtSummary.activeDebt > 0,
      totalDebtAmount: debtSummary.totalDebt,
      activeDebtAmount: debtSummary.activeDebt,
      lastPaymentDate: debtSummary.lastPaymentDate
    });
  } catch (error) {
    console.warn('⚠️ سيتم تجاهل خطأ تحديث بيانات العميل والمتابعة:', error);
  }
};

export const addDebtPayment = async (
  debtId: string,
  amount: number,
  paymentMethod: string,
  receivedBy: string,
  receivedByName: string,
  notes?: string,
  paymentDate?: Date
): Promise<DebtPayment> => {
  try {
    const debt = await getDebtById(debtId);
    if (!debt) throw new Error('الدين غير موجود');

    const calculatedRemaining = debt.amount - debt.paidAmount;
    const actualRemaining = Math.max(0, calculatedRemaining);

    if (amount <= 0) throw new Error('يجب أن يكون المبلغ أكبر من صفر');
    if (amount > actualRemaining) throw new Error(`المبلغ أكبر من المبلغ المتبقي (${actualRemaining.toLocaleString()} ج.م)`);
    if (actualRemaining <= 0) throw new Error('هذا الدين مسدد بالكامل بالفعل');

    const actualPaymentDate = paymentDate || new Date();
    const newPaidAmount = debt.paidAmount + amount;
    const newRemainingAmount = debt.amount - newPaidAmount;
    let newStatus = debt.status;
    if (newRemainingAmount <= 0) newStatus = DebtStatus.PAID;
    else if (newPaidAmount > 0) newStatus = DebtStatus.PARTIALLY_PAID;

    const paymentData: DebtPayment = {
      id: '',
      debtId,
      amount,
      paymentDate: actualPaymentDate,
      paymentMethod,
      receivedBy,
      receivedByName,
      notes: notes || '',
      createdAt: new Date()
    };

    if (isLocalMode()) {
      const newPayment: DebtPayment = { ...paymentData, id: generateLocalId('pay') };
      const payments = getLocalList<DebtPayment>(PAYMENTS_LS_KEY);
      payments.push(newPayment);
      setLocalList(PAYMENTS_LS_KEY, payments);

      const debts = getLocalList<Debt>(DEBTS_LS_KEY);
      const idx = debts.findIndex(d => d.id === debtId);
      if (idx >= 0) {
        debts[idx] = { ...debts[idx], paidAmount: newPaidAmount, remainingAmount: newRemainingAmount, status: newStatus, updatedAt: new Date() };
        setLocalList(DEBTS_LS_KEY, debts);
      }

      try { await updateCustomerDebtInfo(debt.customerId); } catch {}

      const shouldAddToTreasury = paymentMethod === 'cash' || paymentMethod === 'نقدي' ||
                                  paymentMethod === 'vodafone_wallet' || paymentMethod === 'instapay' || paymentMethod === 'etisalat_wallet';
      if (shouldAddToTreasury) {
        addDebtPaymentToTreasury({
          debtId, amount, customerName: debt.customerName, customerId: debt.customerId,
          receivedBy, receivedByName, paymentDate: actualPaymentDate, paymentMethod,
          notes: notes || `سداد دين - ${debt.customerName}`
        }).catch(() => {});
      }

      notifyDebtUpdate();
      return newPayment;
    }

    const paymentRef = await addDoc(getDebtPaymentsCollection(), {
      ...paymentData,
      paymentDate: Timestamp.fromDate(actualPaymentDate),
      createdAt: serverTimestamp()
    });

    const debtRef = doc(ensureFirestore(), 'debts', debtId);
    await updateDoc(debtRef, {
      paidAmount: newPaidAmount,
      remainingAmount: newRemainingAmount,
      status: newStatus,
      updatedAt: serverTimestamp()
    });

    try { await updateCustomerDebtInfo(debt.customerId); } catch {}

    const shouldAddToTreasury = paymentMethod === 'cash' || paymentMethod === 'نقدي' ||
                                paymentMethod === 'vodafone_wallet' || paymentMethod === 'instapay' || paymentMethod === 'etisalat_wallet';
    if (shouldAddToTreasury) {
      try {
        await addDebtPaymentToTreasury({
          debtId, amount, customerName: debt.customerName, customerId: debt.customerId,
          receivedBy, receivedByName, paymentDate: actualPaymentDate, paymentMethod,
          notes: notes || `سداد دين - ${debt.customerName}`
        });
      } catch {}
    }

    notifyDebtUpdate();

    return { ...paymentData, id: paymentRef.id };
  } catch (error) {
    console.error('Error adding debt payment:', error);
    throw error;
  }
};

export const testDebtPaymentLogic = async () => {
  return { success: true, message: 'تم اختبار منطق معالجة دفعات الديون بنجاح', details: {} };
};

export const validateAndFixNegativeDebtPayments = async () => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<any>(DRAWER_TX_LS_KEY);
      let found = 0, fixed = 0;
      for (let i = 0; i < list.length; i++) {
        if ((list[i].amount || 0) < 0) {
          found++;
          list[i] = { ...list[i], amount: Math.abs(list[i].amount), updatedAt: new Date() };
          fixed++;
        }
      }
      if (fixed > 0) setLocalList(DRAWER_TX_LS_KEY, list);
      return { found, fixed, errors: [] };
    }

    const firestore = ensureFirestore();
    const drawerCollection = collection(firestore, 'drawerTransactions');
    const debtPaymentsQuery = query(drawerCollection, where('type', 'in', ['debt_payment', 'maintenance_payment']));
    const snapshot = await getDocs(debtPaymentsQuery);

    let found = 0, fixed = 0;
    const errors: string[] = [];

    for (const docSnapshot of snapshot.docs) {
      const data = docSnapshot.data();
      const amount = data.amount || 0;
      if (amount < 0) {
        found++;
        try {
          const positiveAmount = Math.abs(amount);
          await updateDoc(doc(firestore, 'drawerTransactions', docSnapshot.id), {
            amount: positiveAmount,
            updatedAt: serverTimestamp()
          });
          fixed++;
        } catch (fixError) {
          errors.push(`فشل في إصلاح الدفعة ${docSnapshot.id}: ${fixError}`);
        }
      }
    }
    return { found, fixed, errors };
  } catch (error) {
    return { found: 0, fixed: 0, errors: [`خطأ عام: ${error}`] };
  }
};

export const getDebtPayments = async (debtId: string): Promise<DebtPayment[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<DebtPayment>(PAYMENTS_LS_KEY)
        .filter(p => p.debtId === debtId)
        .sort((a, b) => toDate(b.paymentDate).getTime() - toDate(a.paymentDate).getTime());
    }

    const q = query(getDebtPaymentsCollection(), where('debtId', '==', debtId), orderBy('paymentDate', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        paymentDate: toDate(data.paymentDate),
        createdAt: toDate(data.createdAt)
      } as DebtPayment;
    });
  } catch (error) {
    console.error('Error getting debt payments:', error);
    return [];
  }
};

export const getCustomerDebtPayments = async (customerId: string): Promise<DebtPayment[]> => {
  try {
    const debts = await getCustomerDebts(customerId);
    const debtIds = debts.map(d => d.id);
    if (debtIds.length === 0) return [];

    const payments: DebtPayment[] = [];
    for (const debtId of debtIds) {
      const debtPayments = await getDebtPayments(debtId);
      payments.push(...debtPayments);
    }
    return payments.sort((a, b) => toDate(b.paymentDate).getTime() - toDate(a.paymentDate).getTime());
  } catch (error) {
    console.error('Error getting customer debt payments:', error);
    return [];
  }
};

export const cancelDebt = async (debtId: string, reason: string): Promise<void> => {
  try {
    const debt = await getDebtById(debtId);
    if (!debt) throw new Error('الدين غير موجود');

    if (isLocalMode()) {
      const list = getLocalList<Debt>(DEBTS_LS_KEY);
      const idx = list.findIndex(d => d.id === debtId);
      if (idx >= 0) {
        list[idx] = {
          ...list[idx],
          status: DebtStatus.CANCELLED,
          notes: `${list[idx].notes} | تم إلغاء الدين: ${reason}`,
          updatedAt: new Date()
        };
        setLocalList(DEBTS_LS_KEY, list);
      }
      await updateCustomerDebtInfo(debt.customerId);
      return;
    }

    const debtRef = doc(ensureFirestore(), 'debts', debtId);
    await updateDoc(debtRef, {
      status: DebtStatus.CANCELLED,
      notes: `${debt.notes} | تم إلغاء الدين: ${reason}`,
      updatedAt: serverTimestamp()
    });
    await updateCustomerDebtInfo(debt.customerId);
  } catch (error) {
    console.error('Error cancelling debt:', error);
    throw error;
  }
};

export const updateDebt = async (
  debtId: string,
  updates: Partial<Omit<Debt, 'id' | 'createdAt' | 'customerId' | 'customerName' | 'customerPhone' | 'sourceType' | 'sourceId' | 'sourceReference'>>
): Promise<void> => {
  try {
    const debt = await getDebtById(debtId);
    if (!debt) throw new Error('الدين غير موجود');

    if (isLocalMode()) {
      const list = getLocalList<Debt>(DEBTS_LS_KEY);
      const idx = list.findIndex(d => d.id === debtId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...updates, updatedAt: new Date() };
        setLocalList(DEBTS_LS_KEY, list);
      }
      if (updates.amount !== undefined || updates.status !== undefined) {
        await updateCustomerDebtInfo(debt.customerId);
      }
      return;
    }

    const debtRef = doc(ensureFirestore(), 'debts', debtId);
    await updateDoc(debtRef, { ...updates, updatedAt: serverTimestamp() });
    if (updates.amount !== undefined || updates.status !== undefined) {
      await updateCustomerDebtInfo(debt.customerId);
    }
  } catch (error) {
    console.error('Error updating debt:', error);
    throw error;
  }
};

export const createDebtFromMaintenance = async (
  maintenanceId: string, orderNumber: number, customerId: string, amount: number,
  description: string, createdBy: string, createdByName: string, branchId: string, branchName: string,
  dueDate?: Date, notes?: string
): Promise<Debt> => {
  return createDebt(customerId, amount, DebtSourceType.MAINTENANCE, maintenanceId, orderNumber.toString(),
    description, createdBy, createdByName, branchId, branchName, dueDate, notes);
};

export const createDebtFromSale = async (
  saleId: string, customerId: string, amount: number, description: string,
  createdBy: string, createdByName: string, branchId: string, branchName: string,
  dueDate?: Date, notes?: string
): Promise<Debt> => {
  return createDebt(customerId, amount, DebtSourceType.SALE, saleId, saleId.substring(0, 8),
    description, createdBy, createdByName, branchId, branchName, dueDate, notes);
};

export const searchDebts = async (searchTerm: string): Promise<Debt[]> => {
  try {
    if (!searchTerm || searchTerm.trim() === '') return [];
    const allDebts = await getAllDebts();
    const term = searchTerm.toLowerCase();
    return allDebts.filter(d =>
      d.customerName.toLowerCase().includes(term) ||
      d.customerPhone.includes(searchTerm) ||
      d.sourceReference.includes(searchTerm) ||
      d.description.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching debts:', error);
    return [];
  }
};

const addDebtPaymentToTreasury = async (paymentData: {
  debtId: string; amount: number; customerName: string; customerId: string;
  receivedBy: string; receivedByName: string; paymentDate: Date;
  paymentMethod: string; notes: string;
}) => {
  try {
    const positiveAmount = Math.abs(paymentData.amount);
    const transaction = {
      id: '',
      type: 'debt_payment',
      amount: positiveAmount,
      description: paymentData.notes,
      debtId: paymentData.debtId,
      customerId: paymentData.customerId,
      customerName: paymentData.customerName,
      employeeId: paymentData.receivedBy,
      employeeName: paymentData.receivedByName,
      paymentMethod: paymentData.paymentMethod,
      branchId: 'default',
      paymentDate: paymentData.paymentDate,
      createdAt: new Date(),
      date: paymentData.paymentDate.toISOString().split('T')[0]
    };

    if (isLocalMode()) {
      const newTx = { ...transaction, id: generateLocalId('drawer') };
      const list = getLocalList<any>(DRAWER_TX_LS_KEY);
      list.push(newTx);
      setLocalList(DRAWER_TX_LS_KEY, list);
      return;
    }

    const firestore = ensureFirestore();
    const drawerCollection = collection(firestore, 'drawerTransactions');
    const docRef = await addDoc(drawerCollection, {
      ...transaction,
      paymentDate: Timestamp.fromDate(paymentData.paymentDate),
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    console.error('❌ خطأ في إضافة مدفوعة الدين إلى الخزينة:', error);
    throw error;
  }
};

export const backfillMaintenanceDebtPaymentToTreasury = async (
  params: { orderNumber: number | string; customerName: string; dateISO: string }
): Promise<{ created: number; skipped: number; details: any[] }> => {
  const { orderNumber, customerName, dateISO } = params;
  try {
    const orderStr = String(orderNumber);
    const debts = (isLocalMode() ? getLocalList<Debt>(DEBTS_LS_KEY) : (await getDocs(getDebtsCollection())).docs.map(d => ({ id: d.id, ...d.data() }) as Debt))
      .filter(d => d.sourceType === DebtSourceType.MAINTENANCE && d.sourceReference === orderStr && (d.customerName || '').trim() === customerName.trim());

    if (debts.length === 0) {
      throw new Error(`لم يتم العثور على دين لصيانة رقم ${orderStr} باسم العميل ${customerName}`);
    }
    const debt = debts[0];

    const start = new Date(dateISO + 'T00:00:00');
    const end = new Date(dateISO + 'T23:59:59.999');

    const payments = (isLocalMode()
      ? getLocalList<DebtPayment>(PAYMENTS_LS_KEY)
      : (await getDocs(query(getDebtPaymentsCollection(), where('debtId', '==', debt.id)))).docs.map(d => ({ id: d.id, ...d.data() }) as DebtPayment))
      .filter(p => {
        const pd = toDate(p.paymentDate);
        return pd >= start && pd <= end;
      });

    if (payments.length === 0) {
      throw new Error(`لا توجد دفعات مسجلة لهذا الدين في ${dateISO}`);
    }

    let created = 0, skipped = 0;
    const details: any[] = [];

    for (const p of payments) {
      const method = (p.paymentMethod || '').toLowerCase();
      if (method !== 'cash' && method !== 'نقدي') {
        skipped++;
        details.push({ paymentId: p.id, action: 'skip_non_cash' });
        continue;
      }

      const existing = (isLocalMode()
        ? getLocalList<any>(DRAWER_TX_LS_KEY)
        : (await getDocs(query(collection(ensureFirestore(), 'drawerTransactions'), where('type', '==', 'debt_payment'), where('debtId', '==', debt.id), where('date', '==', dateISO)))).docs)
        .filter(x => (isLocalMode() ? x.debtId === debt.id && x.date === dateISO : true));

      if (existing.length > 0) {
        skipped++;
        details.push({ paymentId: p.id, action: 'already_exists' });
        continue;
      }

      await addDebtPaymentToTreasury({
        debtId: debt.id,
        amount: Math.abs(p.amount || 0),
        customerName: debt.customerName,
        customerId: debt.customerId,
        receivedBy: p.receivedBy || 'system',
        receivedByName: p.receivedByName || 'System',
        paymentDate: new Date(dateISO),
        paymentMethod: p.paymentMethod || 'cash',
        notes: p.notes || `سداد دين - ${debt.customerName}`
      });
      created++;
      details.push({ paymentId: p.id, action: 'created' });
    }

    return { created, skipped, details };
  } catch (error) {
    console.error('❌ خطأ أثناء ترحيل دفعة الدين إلى الخزينة:', error);
    throw error;
  }
};

export const getDebtsByCustomer = async (customerId: string): Promise<Debt[]> => {
  return getCustomerDebts(customerId);
};

export const getDebtsByStatus = async (status: DebtStatus): Promise<Debt[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Debt>(DEBTS_LS_KEY)
        .filter(d => d.status === status)
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
    }

    const q = query(getDebtsCollection(), where('status', '==', status), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        dueDate: toDate(data.dueDate)
      } as Debt;
    });
  } catch (error) {
    console.error('Error getting debts by status:', error);
    throw error;
  }
};

export const getDebtsBySourceType = async (sourceType: DebtSourceType): Promise<Debt[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Debt>(DEBTS_LS_KEY)
        .filter(d => d.sourceType === sourceType)
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
    }

    const q = query(getDebtsCollection(), where('sourceType', '==', sourceType), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        dueDate: toDate(data.dueDate)
      } as Debt;
    });
  } catch (error) {
    console.error('Error getting debts by source type:', error);
    throw error;
  }
};

export interface DebtStatsByType {
  totalAmount: number;
  totalRemaining: number;
  totalPaid: number;
  activeCount: number;
  paidCount: number;
  partiallyPaidCount: number;
}

const calcStats = (debts: Debt[], startDate: Date, endDate: Date): DebtStatsByType => {
  const result: DebtStatsByType = {
    totalAmount: 0, totalRemaining: 0, totalPaid: 0,
    activeCount: 0, paidCount: 0, partiallyPaidCount: 0
  };
  for (const debt of debts) {
    const created = toDate(debt.createdAt);
    if (created >= startDate && created <= endDate) {
      result.totalAmount += debt.amount || 0;
      result.totalRemaining += debt.remainingAmount || 0;
      result.totalPaid += debt.paidAmount || 0;
      if (debt.status === DebtStatus.ACTIVE) result.activeCount++;
      else if (debt.status === DebtStatus.PAID) result.paidCount++;
      else if (debt.status === DebtStatus.PARTIALLY_PAID) result.partiallyPaidCount++;
    }
  }
  return result;
};

export const getDebtStatsByType = async (
  sourceType: DebtSourceType, startDate: Date, endDate: Date
): Promise<DebtStatsByType> => {
  try {
    const debts = (await getDebtsBySourceType(sourceType));
    return calcStats(debts, startDate, endDate);
  } catch (error) {
    console.error(`Error getting debt stats for ${sourceType}:`, error);
    throw error;
  }
};

export const getAllDebtStats = async (startDate: Date, endDate: Date) => {
  try {
    const [salesStats, maintenanceStats] = await Promise.all([
      getDebtStatsByType(DebtSourceType.SALE, startDate, endDate),
      getDebtStatsByType(DebtSourceType.MAINTENANCE, startDate, endDate)
    ]);

    const totalStats: DebtStatsByType = {
      totalAmount: salesStats.totalAmount + maintenanceStats.totalAmount,
      totalRemaining: salesStats.totalRemaining + maintenanceStats.totalRemaining,
      totalPaid: salesStats.totalPaid + maintenanceStats.totalPaid,
      activeCount: salesStats.activeCount + maintenanceStats.activeCount,
      paidCount: salesStats.paidCount + maintenanceStats.paidCount,
      partiallyPaidCount: salesStats.partiallyPaidCount + maintenanceStats.partiallyPaidCount
    };

    return { sales: salesStats, maintenance: maintenanceStats, total: totalStats };
  } catch (error) {
    console.error('Error getting all debt stats:', error);
    throw error;
  }
};
